import './messageHandlers/register';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { setMachines, setSelectedMachine } from '@reducers/machines';
import { injectConditionalObject, isExpired, JOB_STATUSES, machineStatus, navigationRef, ncFileStatuses } from '@helpers';
import { operationsSearch, setShouldRefetchData } from '@reducers/search';
import { setBatch, updateBatchListLocally, updateBatchListWithFetchCall, updateBatchWithFetchCall, updateSingleBatchLocally } from '@reducers/batch';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { formatPerspectiveName } from '@services/tracking/helpers';
import { removeMessageById, selectMessagesByKey } from '@reducers/v2/messagesBus/messagesBus.slice';
import { ProductionInformationMessageType } from '@reducers/v2/messagesBus/types';
import { addedProductionInformationMessageProducer } from './messageProducers/addedProductionInformationMessageProducer';
import { productionBatchStatistics } from '@reducers/statistics';
import { onClosedProductionOperation, onFailedToCloseProductionOperation } from '@reducers/v2/planboard/planboard.slice';
import { CLOSE_PRODUCTION_OPERATION_ERROR_CODES } from '@v2/components/FinishJobModal/constants';
import { Perspective } from '@v2/pages/common/enums';
import type { RootState, AppThunk } from '@store/types';
import type { AppDispatch } from '@reducers/utils/types';

import type {
  DetailsState,
  HeaderConfig,
  TabHistoryEntry,
  PerspectiveTabHistoryEntry,
  ReplaceElementPerspectiveTabHistoryPayload,
  NameValueEntry,
  EditingPartDataPayload,
  UpdateJobMachinesPayload,
} from './types';
import { MachineLink, OperationSearchItem, PartSearchItem, ProductionOrderSearchItem } from '@interfaces/search';
import { NcFile } from '@api/domain';
import { Machine } from '@interfaces/machines';

// Re-export types for consumers
export type {
  DetailsState,
  HeaderConfig,
  TabHistoryEntry,
  PerspectiveTabHistoryEntry,
  ReplaceElementPerspectiveTabHistoryPayload,
  NameValueEntry,
  EditingPartDataPayload,
  UpdateJobMachinesPayload,
} from './types';

const initStateHeaderConfig: HeaderConfig = {
  levels: [],
  part: {
    name: '',
    id: '',
  },
  batch: '',
  job: '',
  productionOrder: '',
  ncFile: '',
  ncFileId: '',
  ncFileStatus: '',
  tools: '',
};

const initState: DetailsState = {
  headerConfig: initStateHeaderConfig,
  machinesHeaderConfig: initStateHeaderConfig,
  ncFileId: null,
  partId: null,
  jobId: null,
  productionOrderId: null,
  ncFileData: {
    data: null,
    wasLoaded: false,
  },
  ncFiles: {
    data: [],
    wasLoaded: false,
  },
  parts: {
    data: [],
    wasLoaded: false,
  },
  jobs: {
    data: [],
    wasLoaded: false,
  },
  productionOrderJobsList: {
    data: [],
    wasLoaded: false,
  },
  productionOrders: {
    data: [],
    wasLoaded: false,
  },
  displayMode: null,
  perspectiveTabHistory: [],
  tabHistory: [],
  forceUpdateHeaderConfig: false,
  ncFileContentSize: 0,
  ncFileUploadDocumentPickerOpened: false,
  isEditModeForPartSwitchOn: false,
  editingPartData: {},
  prevPerspectiveNameValueHistory: [],
  selectedNcFileVersionId: null,
  lastFinishedJobSnapshot: null,
};

// === Selectors ===

export const selectHeaderConfig = (state: RootState) => state.details.headerConfig;
export const selectMachinesHeaderConfig = (state: RootState) => state.details.machinesHeaderConfig;
export const selectNcFileData = (state: RootState) => state.details.ncFileData;
export const selectNcFiles = (state: RootState) => state.details.ncFiles;
export const selectJobsData = (state: RootState) => state.details.jobs;
export const selectJobId = (state: RootState) => state.details.jobId;
export const selectPartsData = (state: RootState) => state.details.parts;
export const selectPartId = (state: RootState) => state.details.partId;
export const selectNcFileId = (state: RootState) => state.details.ncFileId;
export const selectProductionOrderData = (state: RootState) => state.details.productionOrders;
export const selectProductionOrderJobsList = (state: RootState) => state.details.productionOrderJobsList;
export const selectDisplayMode = (state: RootState) => state.details.displayMode;
export const selectProductionOrderId = (state: RootState) => state.details.productionOrderId;
export const selectForceUpdateHeaderConfig = (state: RootState) => state.details.forceUpdateHeaderConfig;
export const selectTabHistory = (state: RootState) => state.details.tabHistory;
export const selectPerspectiveTabHistory = (state: RootState) => state.details.perspectiveTabHistory;
export const selectPrevPerspectiveNameValueHistory = (state: RootState) => state.details.prevPerspectiveNameValueHistory;
export const selectNcFileUploadDocumentPickerOpened = (state: RootState) => state.details.ncFileUploadDocumentPickerOpened;
export const selectSelectedNcFileVersionId = (state: RootState) => state.details.selectedNcFileVersionId;
export const selectIsEditModeForPartSwitchOn = (state: RootState) => state.details.isEditModeForPartSwitchOn;
export const selectEditingPartData = (state: RootState) => state.details.editingPartData;

// === Actions ===

export const setHeaderConfig = createAction<HeaderConfig>('setHeaderConfig');
export const setMachinesHeaderConfigAction = createAction<HeaderConfig>('setMachinesHeaderConfigAction');
export const setNcFileDataAction = createAction<NcFile>('setNcFileDataAction');
export const setNcFilesAction = createAction<NcFile[]>('setNcFilesAction');
export const setJobsAction = createAction<OperationSearchItem[]>('setJobsAction');
export const setProductionOrdersAction = createAction<ProductionOrderSearchItem[]>('setProductionOrdersAction');
export const setPartsAction = createAction<PartSearchItem[]>('setPartAction');
export const setDisplayModeAction = createAction<string | null>('setDisplayModeAction');
export const addToTabHistoryAction = createAction<TabHistoryEntry>('addToTabHistory');
export const goBackInTabHistoryAction = createAction('goBackInTabHistory');
export const clearTabHistoryAction = createAction('clearTabHistoryAction');
export const addToPerspectiveTabHistoryAction = createAction<PerspectiveTabHistoryEntry>('addToPerspectiveTabHistoryAction');
export const replaceElementPerspectiveTabHistoryAction = createAction<ReplaceElementPerspectiveTabHistoryPayload>(
  'replaceElementPerspectiveTabHistoryAction'
);
export const goBackInPerspectiveTabHistoryAction = createAction('goBackInPerspectiveTabHistoryAction');
export const clearPerspectiveTabHistoryAction = createAction('clearPerspectiveTabHistoryAction');
export const forceUpdateHeaderConfigAction = createAction<boolean>('forceUpdateHeaderConfigAction');
export const setNcFileIdAction = createAction<number | null>('setNcFileIdAction');
export const setPartIdAction = createAction<number | null>('setPartIdAction');
export const setJobIdAction = createAction<number | null>('setJobIdAction');
export const setProductionOrderIdAction = createAction<number | null>('setProductionOrderIdAction');
export const clearHeaderConfigAction = createAction('clearHeaderConfigAction');
export const setNCFileContentSizeAction = createAction<number>('setNCFileContentSizeAction');
export const setNcFileUploadDocumentPickerOpenedAction = createAction<boolean>('setNcFileUploadDocumentPickerOpenedAction');
export const updateJobMachinesAction = createAction<UpdateJobMachinesPayload>('updateJobMachinesAction');
export const setProductionOrderJobsListAction = createAction<OperationSearchItem[]>('setProductionOrderJobsListAction');
export const setIsEditModeForPartSwitchOnAction = createAction<boolean>('setIsEditModeForPartSwitchOnAction');
export const setEditingPartDataAction = createAction<EditingPartDataPayload>('setEditingPartDataAction');
export const addToPrevPerspectiveNameValueHistoryAction = createAction<NameValueEntry>('addToPrevPerspectiveNameValueHistoryAction');
export const setSelectedNcFileVersionIdAction = createAction<number | null>('setSelectedNcFileVersionIdAction');

// === Thunks ===

export const updateHeaderConfig =
  (headerConfig: HeaderConfig): AppThunk =>
  (dispatch) => {
    dispatch(setHeaderConfig(headerConfig));
  };

export const updateMachinesHeaderConfig =
  (headerConfig: HeaderConfig): AppThunk =>
  (dispatch) => {
    dispatch(setMachinesHeaderConfigAction(headerConfig));
  };

export const addToTabHistory =
  (screen: TabHistoryEntry): AppThunk =>
  (dispatch) => {
    dispatch(addToTabHistoryAction(screen));
  };

export const goBackInTabHistory = (): AppThunk => (dispatch) => {
  dispatch(goBackInTabHistoryAction());
};

export const clearTabHistory = (): AppThunk => (dispatch) => {
  dispatch(clearTabHistoryAction());
};

export const addToPerspectiveTabHistory =
  (screen: PerspectiveTabHistoryEntry): AppThunk =>
  (dispatch, getState) => {
    const { details } = getState();
    const history = details.perspectiveTabHistory;
    const previousPerspective = history[history.length - 1];

    // Extract perspective name
    const getPerspectiveName = (obj: PerspectiveTabHistoryEntry | undefined) => {
      if (!obj) return null;
      return obj.key || obj.perspective || obj.name || null;
    };

    const fromPerspective = getPerspectiveName(previousPerspective);
    const toPerspective = getPerspectiveName(screen);

    dispatch(addToPerspectiveTabHistoryAction(screen));

    // only include From if it exists (first navigation has no previous perspective)
    const eventProps: Record<string, string | null> = { To: toPerspective };
    if (fromPerspective) {
      eventProps.From = fromPerspective;
    }

    trackEvent(TRACKING_EVENTS.INFO_BAR, {
      action: 'perspectiveChanged',
      fromPerspective: formatPerspectiveName(fromPerspective || ''),
      toPerspective: formatPerspectiveName(toPerspective || ''),
    });
  };

export const replaceElementPerspectiveTabHistory =
  (params: ReplaceElementPerspectiveTabHistoryPayload): AppThunk =>
  (dispatch) => {
    dispatch(replaceElementPerspectiveTabHistoryAction(params));
  };

export const goBackInPerspectiveTabHistory = (): AppThunk => (dispatch) => {
  dispatch(goBackInPerspectiveTabHistoryAction());
};

export const clearPerspectiveTabHistory = (): AppThunk => (dispatch) => {
  dispatch(clearPerspectiveTabHistoryAction());
};

export const setNcFileData =
  (ncFileData: NcFile): AppThunk =>
  (dispatch) => {
    dispatch(setNcFileDataAction(ncFileData));
  };

export const setDisplayMode =
  (displayMode: string | null): AppThunk =>
  (dispatch) => {
    dispatch(setDisplayModeAction(displayMode));
  };

export const setNcFiles =
  (ncFiles: NcFile[]): AppThunk =>
  (dispatch) => {
    dispatch(setNcFilesAction(ncFiles));
  };

export const setParts =
  (parts: PartSearchItem[]): AppThunk =>
  (dispatch) => {
    dispatch(setPartsAction(parts));
  };

export const setJobs =
  (jobs: OperationSearchItem[]): AppThunk =>
  (dispatch) => {
    dispatch(setJobsAction(jobs));
  };

export const setProductionOrders =
  (productionOrders: ProductionOrderSearchItem[]): AppThunk =>
  (dispatch) => {
    dispatch(setProductionOrdersAction(productionOrders));
  };

export const forceUpdateHeaderConfig = (): AppThunk => (dispatch, getState) => {
  const prevForceUpdate = getState().details.forceUpdateHeaderConfig;
  dispatch(forceUpdateHeaderConfigAction(!prevForceUpdate));
};

export const setNcFileId =
  (id: number | null): AppThunk =>
  (dispatch) => {
    dispatch(setNcFileIdAction(id));
  };

export const setPartId =
  (id: number | null): AppThunk =>
  (dispatch) => {
    dispatch(setPartIdAction(id));
  };

export const setJobId =
  (id: number | null): AppThunk =>
  (dispatch) => {
    dispatch(setJobIdAction(id));
  };

export const setProductionOrderId =
  (id: number | null): AppThunk =>
  (dispatch) => {
    dispatch(setProductionOrderIdAction(id));
  };

export const setNCFileContentSize =
  (contentSize: number): AppThunk =>
  (dispatch) => {
    dispatch(setNCFileContentSizeAction(contentSize));
  };

export const setNcFileUploadDocumentPickerOpened =
  (state: boolean): AppThunk =>
  (dispatch) => {
    dispatch(setNcFileUploadDocumentPickerOpenedAction(state));
  };

export const setProductionOrderJobsList =
  (jobs: OperationSearchItem[]): AppThunk =>
  (dispatch) => {
    dispatch(setProductionOrderJobsListAction(jobs));
  };

export const setIsEditModeForPartSwitchOn =
  (state: boolean): AppThunk =>
  (dispatch) => {
    dispatch(setIsEditModeForPartSwitchOnAction(state));
  };

export const setEditingPartData =
  (partId: number | null, partDetails: PartSearchItem | null): AppThunk =>
  (dispatch) => {
    dispatch(setEditingPartDataAction({ partId, partDetails }));
  };

export const addToPrevPerspectiveNameValueHistory =
  (nameValue: NameValueEntry): AppThunk =>
  (dispatch) => {
    dispatch(addToPrevPerspectiveNameValueHistoryAction(nameValue));
  };

export const setSelectedNcFileVersionId =
  (id: number | null): AppThunk =>
  (dispatch) => {
    dispatch(setSelectedNcFileVersionIdAction(id));
  };

export const updateNCFileAfterChangingType =
  (ncFileId: number, ncFileStatus: string, machineStatusValue: string): AppThunk =>
  (dispatch, getState) => {
    const ncFileData = getState()?.details?.ncFileData;
    const selectedMachine = getState()?.machines?.selectedItem;
    const machines = getState()?.machines?.machines;

    if (ncFileData?.data?.ncFileId === ncFileId) {
      dispatch(setNcFileData({ ...ncFileData?.data, ncFileStatus } as Record<string, unknown>));
    }

    if (selectedMachine?.machineStatus?.ncFileId === ncFileId) {
      dispatch(
        setSelectedMachine({
          ...selectedMachine,
          machineStatus: { ...selectedMachine?.machineStatus, ncFileStatus, status: machineStatusValue },
        })
      );

      const updatedMachines = machines.map((machine) =>
        (machine as Record<string, unknown>)?.id === selectedMachine?.id
          ? { ...machine, machineStatus: { ...machine?.machineStatus, ncFileStatus, status: machineStatusValue } }
          : machine
      );

      dispatch(setMachines(updatedMachines));
    }
  };

export const onSetChangeProductionInformation =
  (e: Record<string, unknown>): AppThunk =>
  (dispatch) => {
    const ncFileId = (e?.clampingDetails as Record<string, unknown>)?.ncFileId as number | undefined;
    if (ncFileId) {
      dispatch(updateNCFileAfterChangingType(ncFileId, ncFileStatuses.Change, machineStatus.CHANGE));
    }
  };

export const onSetSetupProductionInformation =
  (e: Record<string, unknown>): AppThunk =>
  (dispatch) => {
    const ncFileId = (e?.clampingDetails as Record<string, unknown>)?.ncFileId as number | undefined;
    if (ncFileId) {
      dispatch(updateNCFileAfterChangingType(ncFileId, ncFileStatuses.Setup, machineStatus.SETUP));
    }
  };

export const onIgnoredProductionInformation =
  (e: Record<string, unknown>): AppThunk =>
  (dispatch) => {
    const ncFileId = (e?.clampingDetails as Record<string, unknown>)?.ncFileId as number | undefined;
    if (ncFileId) {
      dispatch(updateNCFileAfterChangingType(ncFileId, ncFileStatuses.Ignored, machineStatus.NOTPRODUCTIVE));
    }
  };

export const onRemovingPI =
  (e: Record<string, unknown>): AppThunk =>
  (dispatch, getState) => {
    const ncFileId = e?.ncFileId as number | undefined;

    if (ncFileId == null) {
      return;
    }

    const currentRoute = navigationRef?.current?.getCurrentRoute();
    const ncFileStatusParam = (currentRoute?.params as Record<string, unknown>)?.ncFileStatus;
    if (ncFileStatusParam) {
      navigationRef?.current?.setParams({ ncFileStatus: 'Unknown' });
    }

    const ncFileData = getState()?.details?.ncFileData;
    const selectedMachine = getState()?.machines?.selectedItem;
    const machines = getState()?.machines?.machines;
    const batchData = getState()?.batch?.batch;

    if (ncFileData && ncFileData.data?.ncFileId === ncFileId) {
      dispatch(setNcFileData({ ...ncFileData.data, ncFileStatus: ncFileStatuses.Unknown } as Record<string, unknown>));
    }

    if (batchData && batchData?.data?.ncFileId === ncFileId) {
      dispatch(setBatch({ ...batchData?.data, ncFileStatus: ncFileStatuses.Unknown, links: [] }));
    }

    if (selectedMachine && selectedMachine?.machineStatus?.ncFileId === ncFileId) {
      const updatedMachine = { ...selectedMachine, machineStatus: { ...selectedMachine?.machineStatus, ncFileStatus: ncFileStatuses.Unknown } };
      dispatch(setSelectedMachine(updatedMachine));
      dispatch(
        setMachines(
          machines.map((machine) => ((machine as Record<string, unknown>).machineId === selectedMachine.machineId ? updatedMachine : machine))
        )
      );
    }
  };

export const clearHeaderConfig = (): AppThunk => (dispatch) => {
  dispatch(clearHeaderConfigAction());
};

export const updateJobMachines =
  (productionOperationId: number, machines: MachineLink[]): AppThunk =>
  (dispatch) => {
    dispatch(updateJobMachinesAction({ productionOperationId, machines }));
  };

interface LoadJobsParams {
  jobIds: string[];
  filters?: Record<string, unknown>;
  signal?: AbortSignal | null;
}

export const loadJobs =
  ({ jobIds, filters, signal = null }: LoadJobsParams): AppThunk<Promise<Record<string, unknown> | undefined>> =>
  async (dispatch) => {
    if (!jobIds?.length) return;

    const _filters = filters ?? {};

    const jobsData = await dispatch(
      operationsSearch({
        data: _filters,
        filter: { oDataFilter: `ProductionOperationId eq ${jobIds.join(' or ProductionOperationId eq ')}`, orderBy: ['ModifiedOn desc'] },
        select: undefined,
        resetHistory: true,
        ignoreHistory: true,
        signal: signal || undefined,
      })
    );

    if (jobsData?.searchResult?.length) {
      dispatch(setJobs(jobsData.searchResult));
      if (jobsData?.searchResult.length === 1) {
        dispatch(setJobId(jobsData?.searchResult?.[0]?.productionOperationId || null));
      }
    }

    return jobsData;
  };

export const onAddedPI =
  (e: Record<string, unknown>): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const batchId = e.productionBatchId as number | undefined;
    const ncFileId = (e?.clampingDetails as Record<string, unknown>)?.ncFileId as number | undefined;
    const part = e?.part as { partId?: number | null; partName?: string | null } | undefined;

    dispatch(addedProductionInformationMessageProducer(e));

    const ncFileData = getState()?.details?.ncFileData?.data;
    const selectedMachine = getState()?.machines?.selectedItem;
    const machines = getState()?.machines?.machines;

    if (!batchId) {
      if ((ncFileData as Record<string, unknown>)?.ncFileId === ncFileId) {
        dispatch(setNcFileData({ ...(ncFileData as Record<string, unknown>), ncFileStatus: ncFileStatuses.Linked }));
        dispatch(setShouldRefetchData(true));
      }
    }

    if (selectedMachine?.machineStatus?.ncFileId === ncFileId) {
      const newPartId = part?.partId ?? undefined;
      const newPartName = part?.partName ?? undefined;
      const updatedMachine: Machine = {
        ...selectedMachine,
        machineStatus: {
          ...selectedMachine?.machineStatus,
          ncFileStatus: ncFileStatuses.Linked,
          partDetails: {
            ...(selectedMachine?.machineStatus?.partDetails ?? {}),
            ...injectConditionalObject(newPartId != null, { partId: newPartId }),
            ...injectConditionalObject(newPartName != null, { partName: newPartName }),
          },
        },
      };

      dispatch(setSelectedMachine(updatedMachine));
      dispatch(setMachines(machines.map((m) => (m.machineId === selectedMachine.machineId ? updatedMachine : m))));
    }

    if (batchId) {
      dispatch(updateBatchListLocally(batchId));
      dispatch(updateSingleBatchLocally(batchId));
    }

    if (batchId != null) {
      const updatedBatchFromServer = await dispatch(productionBatchStatistics({ productionBatchId: batchId }));
      if (updatedBatchFromServer) {
        dispatch(updateBatchListWithFetchCall(updatedBatchFromServer));
        dispatch(updateBatchWithFetchCall(updatedBatchFromServer));
      }

      trackEvent(TRACKING_EVENTS.ITEM_LINKING, {
        action: 'linked',
        fromItem: 'batch',
        toItem: 'job',
        result: 'success',
      });
    }

    dispatch(forceUpdateHeaderConfig());

    const currentRoute = navigationRef?.current?.getCurrentRoute();
    const ncFileStatusParam = (currentRoute?.params as Record<string, unknown>)?.ncFileStatus;
    if (ncFileStatusParam === 'Unknown') {
      navigationRef.current?.setParams({ ncFileStatus: 'Linked' });
    }
  };

export const getRecentlyAddedLinks = (): AppThunk<{ recentlyLinkedJobIdsSet: Set<string> }> => (dispatch, getState) => {
  const route = navigationRef?.current?.getCurrentRoute();

  const recentlyLinkedJobIdsSet = new Set<string>();
  const perspective = (route?.params as Record<string, unknown>)?.perspective;

  const rootState = getState();

  const batch = rootState.batch.batch?.data;
  const ncFileData = rootState.details.ncFileData?.data;
  const selectedMachine = rootState.machines.selectedItem;

  const isBatchPerspective = perspective === Perspective.BATCH;

  const ncFileId =
    perspective == null
      ? selectedMachine.machineStatus?.ncFileId
      : isBatchPerspective
      ? batch?.ncFileId
      : (ncFileData as Record<string, unknown>)?.ncFileId;
  const batchId = batch?.productionBatchId;

  const shouldTakeMessageIntoAccount = ({
    ncFileIdFromMessage,
    batchIdFromMessage,
  }: {
    ncFileIdFromMessage: number;
    batchIdFromMessage: number | undefined;
  }) => ncFileId === ncFileIdFromMessage && (batchIdFromMessage == null || batchIdFromMessage === batchId);

  // Access messages directly from state using the selector
  const messages = selectMessagesByKey(rootState, (ncFileId as number) ?? '');

  messages.forEach((message) => {
    // Check if message type is ADDED_PRODUCTION_INFORMATION
    if (message.messageType !== ProductionInformationMessageType.ADDED_PRODUCTION_INFORMATION) {
      return;
    }

    // Check if message is expired
    if (isExpired(message.ttl)) {
      dispatch(removeMessageById({ key: (ncFileId as number) ?? '', messageId: message.id }));
      return;
    }

    const payload = message.payload as Record<string, unknown>;

    // Find the first ncFileSmartLink where ncFileId matches
    const matchingSmartLink = (payload?.ncSmartLinks as Array<Record<string, unknown>>)?.find((smartLink) => smartLink.ncFileId === ncFileId);

    if (!matchingSmartLink) {
      return;
    }

    // Check if we should take this message into account
    const shouldProcess = shouldTakeMessageIntoAccount({
      ncFileIdFromMessage: matchingSmartLink.ncFileId as number,
      batchIdFromMessage: payload.productionBatchId as number | undefined,
    });

    if (shouldProcess) {
      recentlyLinkedJobIdsSet.add(matchingSmartLink.productionOperationId as string);
    }
  });

  return { recentlyLinkedJobIdsSet };
};

// === Reducer ===

export default createReducer<DetailsState>(initState, {
  [setHeaderConfig.type]: (state: DetailsState, action: { payload: HeaderConfig }) => {
    return { ...state, headerConfig: action.payload };
  },
  [setMachinesHeaderConfigAction.type]: (state: DetailsState, action: { payload: HeaderConfig }) => {
    return { ...state, machinesHeaderConfig: action.payload };
  },
  [setNcFileDataAction.type]: (state: DetailsState, action: { payload: NcFile }) => {
    return { ...state, ncFileData: { data: action.payload, wasLoaded: true } };
  },
  [setDisplayModeAction.type]: (state: DetailsState, action: { payload: string | null }) => {
    return { ...state, displayMode: action.payload };
  },
  [setPartsAction.type]: (state: DetailsState, action: { payload: PartSearchItem[] }) => {
    return { ...state, parts: { data: action.payload, wasLoaded: true } };
  },
  [setNcFilesAction.type]: (state: DetailsState, action: { payload: NcFile[] }) => {
    return { ...state, ncFiles: { data: action.payload, wasLoaded: true } };
  },
  [setJobsAction.type]: (state: DetailsState, action: { payload: OperationSearchItem[] }) => {
    return { ...state, jobs: { data: action.payload, wasLoaded: true } };
  },
  [setProductionOrdersAction.type]: (state: DetailsState, action: { payload: ProductionOrderSearchItem[] }) => {
    return { ...state, productionOrders: { data: action.payload, wasLoaded: true } };
  },
  [goBackInTabHistoryAction.type]: (state: DetailsState) => {
    const newHistory = [...state.tabHistory];
    newHistory.pop();

    return {
      ...state,
      tabHistory: newHistory,
    };
  },
  [goBackInPerspectiveTabHistoryAction.type]: (state: DetailsState) => {
    const newHistory = [...state.perspectiveTabHistory];
    newHistory.pop();

    const newNameValueHistory = [...state.prevPerspectiveNameValueHistory];
    newNameValueHistory.pop();

    return {
      ...state,
      perspectiveTabHistory: newHistory,
      prevPerspectiveNameValueHistory: newNameValueHistory,
    };
  },
  [addToTabHistoryAction.type]: (state: DetailsState, action: { payload: TabHistoryEntry }) => {
    const oldHistory = state.tabHistory;
    const newTab = action.payload;

    if (!newTab.value) {
      return;
    }

    const lastTabInHistory = oldHistory.at(-1);

    if (lastTabInHistory && lastTabInHistory?.key === newTab?.key) {
      if (
        lastTabInHistory?.value !== newTab?.value ||
        lastTabInHistory?.shouldDisplayModalTriggerButton !== newTab?.shouldDisplayModalTriggerButton
      ) {
        return { ...state, tabHistory: [...oldHistory.slice(0, -1), newTab] };
      }
      return;
    }

    return {
      ...state,
      tabHistory: [...oldHistory, newTab],
    };
  },
  [addToPerspectiveTabHistoryAction.type]: (state: DetailsState, action: { payload: PerspectiveTabHistoryEntry }) => {
    const oldHistory = state.perspectiveTabHistory;
    const newTab = action.payload;

    if (!newTab.value) {
      return;
    }

    const lastTabInHistory = oldHistory.at(-1);

    if (lastTabInHistory && lastTabInHistory?.key === newTab?.key) {
      if (lastTabInHistory?.value !== newTab?.value) {
        return { ...state, perspectiveTabHistory: [...oldHistory.slice(0, -1), newTab] };
      }
      return;
    }

    return {
      ...state,
      perspectiveTabHistory: [...oldHistory, newTab],
    };
  },
  [replaceElementPerspectiveTabHistoryAction.type]: (state: DetailsState, action: { payload: ReplaceElementPerspectiveTabHistoryPayload }) => {
    const oldHistory = state.perspectiveTabHistory;
    const { newTab, indexToReplace } = action.payload ?? {};

    if (!newTab || !newTab.value) {
      return;
    }

    const index = indexToReplace ?? Math.max(oldHistory.length - 1, 0);
    return { ...state, perspectiveTabHistory: [...oldHistory.toSpliced(index, 1, newTab)] };
  },
  [clearTabHistoryAction.type]: (state: DetailsState) => {
    return {
      ...state,
      tabHistory: [],
    };
  },
  [clearPerspectiveTabHistoryAction.type]: (state: DetailsState) => {
    return {
      ...state,
      perspectiveTabHistory: [],
      prevPerspectiveNameValueHistory: [],
    };
  },
  [forceUpdateHeaderConfigAction.type]: (state: DetailsState, action: { payload: boolean }) => {
    return {
      ...state,
      forceUpdateHeaderConfig: action.payload,
    };
  },
  [setNcFileIdAction.type]: (state: DetailsState, action: { payload: number | null }) => {
    return { ...state, ncFileId: action.payload };
  },
  [setPartIdAction.type]: (state: DetailsState, action: { payload: number | null }) => {
    return { ...state, partId: action.payload };
  },
  [setJobIdAction.type]: (state: DetailsState, action: { payload: number | null }) => {
    return { ...state, jobId: action.payload };
  },
  [setProductionOrderIdAction.type]: (state: DetailsState, action: { payload: number | null }) => {
    return { ...state, productionOrderId: action.payload };
  },
  [clearHeaderConfigAction.type]: (state: DetailsState) => {
    return { ...state, headerConfig: initStateHeaderConfig };
  },
  [setNCFileContentSizeAction.type]: (state: DetailsState, action: { payload: number }) => {
    return { ...state, ncFileContentSize: action.payload };
  },
  [setNcFileUploadDocumentPickerOpenedAction.type]: (state: DetailsState, action: { payload: boolean }) => {
    return { ...state, ncFileUploadDocumentPickerOpened: action.payload };
  },
  [setProductionOrderJobsListAction.type]: (state: DetailsState, action: { payload: OperationSearchItem[] }) => {
    return { ...state, productionOrderJobsList: { data: action.payload, wasLoaded: true } };
  },
  [updateJobMachinesAction.type]: (state: DetailsState, { payload }: { payload: UpdateJobMachinesPayload }) => {
    const { productionOperationId, machines } = payload;
    return {
      ...state,
      jobs: {
        ...state.jobs,
        data: state.jobs.data.map((j) => ((j as Record<string, unknown>).productionOperationId === productionOperationId ? { ...j, machines } : j)),
      },
    };
  },
  [onClosedProductionOperation.type]: (
    state: DetailsState,
    {
      payload,
    }: {
      payload: {
        productionOperationId: number;
        optimisticFields?: {
          actualEndDate: string;
          actualQuantity: number;
        };
      };
    }
  ) => {
    const { productionOperationId, optimisticFields } = payload;
    const idx = state.jobs.data.findIndex((j) => (j as Record<string, unknown>).productionOperationId === productionOperationId);
    if (idx === -1) return state;

    const original = state.jobs.data[idx];
    // Idempotent — SignalR re-confirmation must not clobber user-entered actualEndDate or overwrite the Running snapshot.
    if ((original as Record<string, unknown>).status === JOB_STATUSES.FINISHED) return state;

    // Only fabricate end-date fields when WE did the close — for external closes the backend has the real timestamp; making one up on the client would render differently for every connected user.
    const patched = {
      ...original,
      status: JOB_STATUSES.FINISHED,
      ...(optimisticFields
        ? {
            actualEndDate: optimisticFields.actualEndDate,
            productionEndedOn: optimisticFields.actualEndDate,
            quantityProduced: optimisticFields.actualQuantity,
          }
        : {}),
    };

    // Snapshot is for OUR revert path only — external SignalR closes must not overwrite the slot, or a concurrent broadcast for a different job will displace our pending revert.
    return {
      ...state,
      ...(optimisticFields ? { lastFinishedJobSnapshot: original } : {}),
      jobs: {
        ...state.jobs,
        data: state.jobs.data.map((j, i) => (i === idx ? patched : j)),
      },
    };
  },
  [onFailedToCloseProductionOperation.type]: (
    state: DetailsState,
    {
      payload,
    }: {
      payload: { productionOperationId: number; errorMessage?: string; validationErrors?: { errorCode: string }[] };
    }
  ) => {
    const snapshot = state.lastFinishedJobSnapshot;
    if (!snapshot || (snapshot as Record<string, unknown>).productionOperationId !== payload.productionOperationId) {
      return state;
    }
    // JobAlreadyFinished = success-in-disguise; the job IS finished. Keep optimistic Finished, only drop the snapshot.
    const isAlreadyFinished = payload.validationErrors?.some((e) => e.errorCode === CLOSE_PRODUCTION_OPERATION_ERROR_CODES.JOB_ALREADY_FINISHED);
    if (isAlreadyFinished) {
      return { ...state, lastFinishedJobSnapshot: null };
    }
    return {
      ...state,
      lastFinishedJobSnapshot: null,
      jobs: {
        ...state.jobs,
        data: state.jobs.data.map((j) => ((j as Record<string, unknown>).productionOperationId === payload.productionOperationId ? snapshot : j)),
      },
    };
  },
  [setIsEditModeForPartSwitchOnAction.type]: (state: DetailsState, action: { payload: boolean }) => {
    return { ...state, isEditModeForPartSwitchOn: action.payload };
  },
  [setEditingPartDataAction.type]: (state: DetailsState, action: { payload: EditingPartDataPayload }) => {
    return { ...state, editingPartData: action.payload };
  },
  [addToPrevPerspectiveNameValueHistoryAction.type]: (state: DetailsState, action: { payload: NameValueEntry }) => {
    if (!action.payload?.value) return;

    const prevNameValue = state.prevPerspectiveNameValueHistory.at(-1);

    if (prevNameValue && action.payload?.value === prevNameValue.value && action.payload?.name === prevNameValue.name) {
      return;
    }

    return { ...state, prevPerspectiveNameValueHistory: [...state.prevPerspectiveNameValueHistory, action.payload] };
  },
  [setSelectedNcFileVersionIdAction.type]: (state: DetailsState, action: { payload: number | null }) => {
    return { ...state, selectedNcFileVersionId: action.payload };
  },
});
